import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Lightbulb,
  Loader2,
  Mic,
  Globe,
  BookOpen,
  FileText,
  Youtube,
  PlayCircle,
  ArrowBigUp,
  ArrowBigDown,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Clock,
  Rocket,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  youtube_channel: { label: 'YouTube Channel', icon: Youtube, color: 'text-red-600' },
  youtube_video: { label: 'YouTube Video', icon: PlayCircle, color: 'text-red-500' },
  podcast: { label: 'Podcast', icon: Mic, color: 'text-purple-600' },
  website: { label: 'Website', icon: Globe, color: 'text-blue-600' },
  blog: { label: 'Blog', icon: BookOpen, color: 'text-emerald-600' },
  research: { label: 'Research / Paper', icon: FileText, color: 'text-amber-700' },
};

const STATUS_META = {
  pending: { label: 'Pending Review', icon: Clock, className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  approved: { label: 'Approved', icon: CheckCircle2, className: 'bg-blue-100 text-blue-800 border-blue-300' },
  promoted: { label: 'Live', icon: Rocket, className: 'bg-green-100 text-green-800 border-green-300' },
  rejected: { label: 'Not a fit', icon: XCircle, className: 'bg-red-100 text-red-800 border-red-300' },
};

const SuggestionCard = ({ item, onVote, voting, canVote }) => {
  const meta = TYPE_META[item.source_type] || TYPE_META.website;
  const status = STATUS_META[item.status] || STATUS_META.pending;
  const TypeIcon = meta.icon;
  const StatusIcon = status.icon;

  return (
    <Card data-testid={`suggestion-card-${item.id}`}>
      <CardContent className="pt-6">
        <div className="flex gap-4">
          <div className="flex flex-col items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => onVote(item, item.my_vote === 'up' ? 'clear' : 'up')}
              disabled={!canVote || voting === item.id}
              className={`p-1 rounded hover:bg-muted ${item.my_vote === 'up' ? 'text-green-600' : 'text-muted-foreground'} disabled:opacity-50`}
              data-testid={`vote-up-${item.id}`}
              aria-label="Upvote"
            >
              <ArrowBigUp className="w-6 h-6" />
            </button>
            <span className={`text-sm font-semibold ${item.vote_score > 0 ? 'text-green-600' : item.vote_score < 0 ? 'text-red-600' : ''}`}>
              {item.vote_score || 0}
            </span>
            <button
              type="button"
              onClick={() => onVote(item, item.my_vote === 'down' ? 'clear' : 'down')}
              disabled={!canVote || voting === item.id}
              className={`p-1 rounded hover:bg-muted ${item.my_vote === 'down' ? 'text-red-600' : 'text-muted-foreground'} disabled:opacity-50`}
              data-testid={`vote-down-${item.id}`}
              aria-label="Downvote"
            >
              <ArrowBigDown className="w-6 h-6" />
            </button>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <TypeIcon className={`w-4 h-4 ${meta.color}`} />
              <span className="text-xs text-muted-foreground">{meta.label}</span>
              <Badge variant="outline" className={`text-xs ${status.className}`}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {status.label}
              </Badge>
            </div>
            <h3 className="font-semibold text-lg leading-tight">{item.title}</h3>
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary hover:underline inline-flex items-center gap-1 break-all"
            >
              {item.url} <ExternalLink className="w-3 h-3 shrink-0" />
            </a>
            {item.description && (
              <p className="text-sm text-muted-foreground mt-2 line-clamp-3">{item.description}</p>
            )}
            {item.rationale && (
              <p className="text-xs italic text-muted-foreground mt-1">
                <strong>Why:</strong> {item.rationale}
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              Suggested by <strong>{item.submitted_by_username || 'a member'}</strong>
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function SuggestContent() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const prefillType = searchParams.get('type');
  const prefillUrl = searchParams.get('url') || '';

  const [form, setForm] = useState({
    source_type: Object.keys(TYPE_META).includes(prefillType) ? prefillType : 'youtube_channel',
    url: prefillUrl,
    title: '',
    description: '',
    rationale: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [voting, setVoting] = useState(null);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const params = {};
      if (activeTab !== 'all') params.source_type = activeTab;
      const res = await axios.get(`${API_URL}/api/content-suggestions`, { params });
      setItems(res.data);
    } catch (err) {
      // Silent fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please sign in to submit a suggestion');
      return;
    }
    if (!form.url.trim() || !form.title.trim()) {
      toast.error('URL and title are required');
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API_URL}/api/content-suggestions`, form);
      toast.success('Thanks! Your suggestion was submitted for review.');
      setForm({
        source_type: form.source_type,
        url: '',
        title: '',
        description: '',
        rationale: '',
      });
      fetchItems();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to submit';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (item, direction) => {
    if (!user) {
      toast.info('Please sign in to vote');
      return;
    }
    try {
      setVoting(item.id);
      const res = await axios.post(`${API_URL}/api/content-suggestions/${item.id}/vote`, {
        direction,
      });
      setItems((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, ...res.data } : i)),
      );
    } catch (err) {
      toast.error('Vote failed');
    } finally {
      setVoting(null);
    }
  };

  return (
    <div className="container mx-auto px-4 py-10 max-w-5xl">
      <div className="mb-8">
        <h1 className="text-4xl sm:text-5xl font-heading font-bold flex items-center gap-3">
          <Lightbulb className="w-10 h-10 text-amber-500" />
          Suggest a Source
        </h1>
        <p className="text-base text-muted-foreground mt-2 max-w-2xl">
          Know a great YouTube channel, podcast, blog, or website that AADC members should know
          about? Suggest it here — admins review and promote the best ones into our directories.
        </p>
      </div>

      <div className="grid md:grid-cols-5 gap-6">
        {/* Form */}
        <Card className="md:col-span-2 h-fit" data-testid="suggest-form-card">
          <CardHeader>
            <CardTitle>Submit a Source</CardTitle>
            <CardDescription>Members only — sign in to suggest</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <Label htmlFor="type">Type</Label>
                <Select
                  value={form.source_type}
                  onValueChange={(v) => setForm({ ...form, source_type: v })}
                >
                  <SelectTrigger id="type" data-testid="source-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_META).map(([k, v]) => (
                      <SelectItem key={k} value={k}>
                        {v.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="url">URL *</Label>
                <Input
                  id="url"
                  type="url"
                  placeholder="https://..."
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  data-testid="suggest-url-input"
                  required
                />
              </div>

              <div>
                <Label htmlFor="title">Title / Name *</Label>
                <Input
                  id="title"
                  placeholder="Channel or site name"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="suggest-title-input"
                  required
                />
              </div>

              <div>
                <Label htmlFor="desc">Short description</Label>
                <Textarea
                  id="desc"
                  placeholder="What is it about?"
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="suggest-desc-input"
                />
              </div>

              <div>
                <Label htmlFor="rat">Why is it valuable?</Label>
                <Textarea
                  id="rat"
                  placeholder="Why should AADC members know about this?"
                  rows={2}
                  value={form.rationale}
                  onChange={(e) => setForm({ ...form, rationale: e.target.value })}
                  data-testid="suggest-rationale-input"
                />
              </div>

              {!user && (
                <p className="text-xs text-muted-foreground">
                  <Link to="/login" className="text-primary underline">
                    Sign in
                  </Link>{' '}
                  to submit.
                </p>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={submitting || !user}
                data-testid="suggest-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Submit Suggestion
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Browse */}
        <div className="md:col-span-3 space-y-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
              {Object.entries(TYPE_META).map(([k, v]) => (
                <TabsTrigger key={k} value={k} data-testid={`filter-${k}`}>
                  {v.label}
                </TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value={activeTab} className="mt-4 space-y-3">
              {loading ? (
                <div className="text-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                </div>
              ) : items.length === 0 ? (
                <Card>
                  <CardContent className="pt-6 text-center py-12 text-muted-foreground">
                    No suggestions yet. Be the first!
                  </CardContent>
                </Card>
              ) : (
                items.map((item) => (
                  <SuggestionCard
                    key={item.id}
                    item={item}
                    onVote={handleVote}
                    voting={voting}
                    canVote={!!user}
                  />
                ))
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
